import React, { useState, useEffect, useRef } from 'react';

// Matrix Canvas Animation Component
const MatrixCanvas = () => {
	const canvasRef = useRef(null);
	const animationRef = useRef(null);
	const particlesRef = useRef([]);

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');

		const resizeCanvas = () => {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
		};

		resizeCanvas();

		// Particle class
		class Particle {
			constructor(x, y, directionX, directionY, size) {
				this.x = x;
				this.y = y;
				this.directionX = directionX;
				this.directionY = directionY;
				this.size = size;
			}

			draw() {
				ctx.beginPath();
				ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2, false);
				ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
				ctx.fill();
			}

			update() {
				if (this.x > canvas.width || this.x < 0) {
					this.directionX = -this.directionX;
				}
				if (this.y > canvas.height || this.y < 0) {
					this.directionY = -this.directionY;
				}

				this.x += this.directionX;
				this.y += this.directionY;
				this.draw();
			}
		}

		const initParticles = () => {
			particlesRef.current = [];
			const numberOfParticles = (canvas.height * canvas.width) / 9000;

			for (let i = 0; i < numberOfParticles; i++) {
				const size = Math.random() * 2 + 1;
				const x = Math.random() * (canvas.width - size * 2) + size;
				const y = Math.random() * (canvas.height - size * 2) + size;
				const directionX = (Math.random() * 0.4) - 0.2;
				const directionY = (Math.random() * 0.4) - 0.2;

				particlesRef.current.push(new Particle(x, y, directionX, directionY, size));
			}
		};

		const connect = () => {
			for (let a = 0; a < particlesRef.current.length; a++) {
				for (let b = a; b < particlesRef.current.length; b++) {
					const distance = Math.pow(particlesRef.current[a].x - particlesRef.current[b].x, 2) +
						Math.pow(particlesRef.current[a].y - particlesRef.current[b].y, 2);

					if (distance < (canvas.width / 7) * (canvas.height / 7)) {
						const opacityValue = 1 - (distance / 20000);
						ctx.strokeStyle = `rgba(255, 255, 255, ${opacityValue})`;
						ctx.lineWidth = 1;
						ctx.beginPath();
						ctx.moveTo(particlesRef.current[a].x, particlesRef.current[a].y);
						ctx.lineTo(particlesRef.current[b].x, particlesRef.current[b].y);
						ctx.stroke();
					}
				}
			}
		};

		const animate = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			for (let particle of particlesRef.current) {
				particle.update();
			}
			connect();

			animationRef.current = requestAnimationFrame(animate);
		};

		initParticles();
		animate();

		const handleResize = () => {
			resizeCanvas();
			initParticles();
		};

		window.addEventListener('resize', handleResize);

		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
			}
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	return (
		<canvas
			ref={canvasRef}
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100%',
				height: '100%',
				zIndex: 1,
				pointerEvents: 'none'
			}}
		/>
	);
};

// Home Page Component
const HomePage = ({ isVisible, onNavigate }) => (
	<div className={`page-content ${isVisible ? 'page-visible' : 'page-hidden'}`}>
		<div className="profil-resmi">
			<img src="/profile.jpg" alt="Mermalat" />
		</div>
		<h1>Melih Eren</h1>
		<h2>Bilgisayar Mühendisi</h2>
		<p>Veri bilimi ve makine öğrenmesi alanında kendini geliştirmeye çalışan bir CS 2. sınıf öğrencisi.</p>
		<div className="sosyal-linkler">
			<button className="nav-btn" onClick={() => onNavigate('about')}>
				<i className="fas fa-user-circle"></i> Hakkımda
			</button>
			<button className="nav-btn" onClick={() => onNavigate('blog')}>
				<i className="fas fa-rss"></i> Blog
			</button>
			<button className="nav-btn" onClick={() => onNavigate('signature')}>
				<i className="fas fa-signature"></i> İmza Defteri
			</button>
			<button className="nav-btn" onClick={() => onNavigate('contact')}>
				<i className="fas fa-envelope"></i> İletişim
			</button>
		</div>
	</div>
);

// About Page Component
const AboutPage = ({ isVisible, onNavigate }) => (
	<div className={`page-content ${isVisible ? 'page-visible' : 'page-hidden'}`}>
		<button className="geri-link" onClick={() => onNavigate('home')}>
			← Ana Sayfaya Dön
		</button>

		<h1>Hakkımda</h1>

		<div className="gif-alani">
			<img src="/teto-kasane.gif" alt="Kişisel GIF" />
		</div>

		<div className="hakkimda-yazisi">
			<h2>Ben Kimim?</h2>
			<p>
				Merhaba, ben Mermalat! Veri bilimi ve makine öğrenmesi alanında kendimi geliştiren,
				teknolojiyle iç içe yaşayan bir öğrenciyim. Kod yazmayı, veri analizi yapmayı ve
				yeni teknolojileri keşfetmeyi seviyorum.
			</p>
			<p>
				Bu sitede projelerimi, blog yazılarımı ve deneyimlerimi paylaşıyorum. Amacım,
				öğrendiklerimi başkalarıyla paylaşmak ve birlikte büyüyen bir toplulukta yer almak.
			</p>
			<p>
				Kendime ait daha fazla resim bulundurmak isterdim fakat sosyal medyaya fazla resim yüklemeyi doğru bulmuyorum!
			</p>
		</div>

		<div className="sosyal-medya-bolumu">
			<div className="sosyal-ikonlar-buyuk">
				<a href="https://github.com/Mermalat" title="GitHub" target="_blank" rel="noopener noreferrer">
					<i className="fab fa-github"></i>
				</a>
				<a href="https://www.linkedin.com/in/melih-eren-malli-1619672a9/" title="LinkedIn">
					<i className="fab fa-linkedin"></i>
				</a>
				<a href="https://instagram.com/mermalatt" title="Instagram" target="_blank" rel="noopener noreferrer">
					<i className="fab fa-instagram"></i>
				</a>
			</div>
		</div>
	</div>
);

// Blog Page Component
const BlogPage = ({ isVisible, onNavigate }) => (
	<div className={`page-content ${isVisible ? 'page-visible' : 'page-hidden'}`}>
		<button className="geri-link" onClick={() => onNavigate('home')}>
			← Ana Sayfaya Dön
		</button>

		<header className="blog-baslik">
			<h1>Kod Günlükleri</h1>
			<p>Yazılım, teknoloji ve dijital dünyadan notlar...</p>
		</header>

		<div className="yazi-listesi">
			<article className="yazi-ozeti">
				<h2><span className="link-button">İlk Blog Yazım!</span></h2>
				<p className="yazi-meta">20 Eylül 2025 · ilk blogpost!</p>
				<p>
					Bu siteyi yazmak oldukça zor oldu ve hala daha geliştirmeye çalışıyorum.
				</p>
				<span className="devamini-oku">Devamı yok o yüzden burasıı referanssız! →</span>
			</article>
		</div>
	</div>
);

// Maintenance Page Component (İmza Defteri yerine)
const MaintenancePage = ({ isVisible, onNavigate }) => (
	<div className={`page-content ${isVisible ? 'page-visible' : 'page-hidden'}`}>
		<button className="geri-link" onClick={() => onNavigate('home')}>
			← Ana Sayfaya Dön
		</button>

		<div className="maintenance-container">
			<div className="maintenance-icon">
				<i className="fas fa-tools"></i>
			</div>

			<h1 className="maintenance-title">🚧 Bakım Çalışması</h1>

			<div className="maintenance-content">
				<p className="maintenance-description">
					İmza defteri özelliği şu anda geliştirme aşamasında. Backend entegrasyonu
					tamamlandıktan sonra hizmete sunulacak.
				</p>

				<div className="feature-preview">
					<h3>Yakında gelecek özellikler:</h3>
					<ul className="feature-list">
						<li><i className="fas fa-check-circle"></i> E-posta doğrulaması ile güvenli imza sistemi</li>
						<li><i className="fas fa-check-circle"></i> Gerçek zamanlı imza görüntüleme</li>
						<li><i className="fas fa-check-circle"></i> Spam koruması ve moderasyon</li>
						<li><i className="fas fa-check-circle"></i> Responsive tasarım</li>
					</ul>
				</div>

				<div className="maintenance-status">
					<div className="progress-bar">
						<div className="progress-fill"></div>
					</div>
					<p className="progress-text">Geliştirme İlerlemesi: %85</p>
				</div>

				<div className="contact-suggestion">
					<p>Bu süreçte benimle iletişime geçmek isterseniz:</p>
					<button className="contact-btn" onClick={() => onNavigate('contact')}>
						<i className="fas fa-envelope"></i> İletişim Sayfasını Ziyaret Et
					</button>
				</div>
			</div>
		</div>
	</div>
);

const ContactPage = ({ isVisible, onNavigate }) => {
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		message: ''
	});

	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async () => {
		if (!formData.name || !formData.email || !formData.message) {
			alert('Lütfen tüm alanları doldurun!');
			return;
		}

		setIsSubmitting(true);

		try {
			// FormSubmit.co ile gerçek form gönderimi
			const formElement = document.createElement('form');
			formElement.action = 'https://formsubmit.co/m.erenmalli@hotmail.com';
			formElement.method = 'POST';

			// Form verilerini ekle
			const nameInput = document.createElement('input');
			nameInput.type = 'hidden';
			nameInput.name = 'name';
			nameInput.value = formData.name;

			const emailInput = document.createElement('input');
			emailInput.type = 'hidden';
			emailInput.name = 'email';
			emailInput.value = formData.email;

			const messageInput = document.createElement('input');
			messageInput.type = 'hidden';
			messageInput.name = 'message';
			messageInput.value = formData.message;

			// Yönlendirme sayfası (isteğe bağlı)
			const redirectInput = document.createElement('input');
			redirectInput.type = 'hidden';
			redirectInput.name = '_next';
			redirectInput.value = window.location.origin;

			// Spam koruması
			const captchaInput = document.createElement('input');
			captchaInput.type = 'hidden';
			captchaInput.name = '_captcha';
			captchaInput.value = 'false';

			formElement.appendChild(nameInput);
			formElement.appendChild(emailInput);
			formElement.appendChild(messageInput);
			formElement.appendChild(redirectInput);
			formElement.appendChild(captchaInput);

			document.body.appendChild(formElement);
			formElement.submit();

			// Form gönderildi, alanları temizle
			setFormData({ name: '', email: '', message: '' });

		} catch (error) {
			console.error('Form gönderim hatası:', error);
			alert('Bir hata oluştu. Lütfen tekrar deneyin.');
			setIsSubmitting(false);
		}
	};

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		});
	};

	return (
		<div className={`page-content ${isVisible ? 'page-visible' : 'page-hidden'}`}>
			<button className="geri-link" onClick={() => onNavigate('home')}>
				← Ana Sayfaya Dön
			</button>

			<div className="blog-baslik">
				<h1>İletişim</h1>
				<p>Aşağıdaki formu kullanarak bana ulaşabilirsiniz.</p>
			</div>

			<div className="iletisim-form">
				<div className="form-grup">
					<label htmlFor="name">Adınız</label>
					<input
						type="text"
						id="name"
						name="name"
						value={formData.name}
						onChange={handleChange}
						disabled={isSubmitting}
					/>
				</div>

				<div className="form-grup">
					<label htmlFor="email">E-posta Adresiniz</label>
					<input
						type="email"
						id="email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						disabled={isSubmitting}
					/>
				</div>

				<div className="form-grup">
					<label htmlFor="message">Mesajınız</label>
					<textarea
						id="message"
						name="message"
						value={formData.message}
						onChange={handleChange}
						disabled={isSubmitting}
					/>
				</div>

				<button
					onClick={handleSubmit}
					className={`gonder-btn ${isSubmitting ? 'submitting' : ''}`}
					disabled={isSubmitting}
				>
					{isSubmitting ? 'Gönderiliyor... ⏳' : 'Gönder ✉️'}
				</button>
			</div>
		</div>
	);
};

// Main App Component
const App = () => {
	const [currentPage, setCurrentPage] = useState('home');
	const [isTransitioning, setIsTransitioning] = useState(false);

	const navigateTo = (page) => {
		if (page === currentPage || isTransitioning) return;

		setIsTransitioning(true);

		// Yumuşak geçiş animasyonu
		setTimeout(() => {
			setCurrentPage(page);
			setTimeout(() => {
				setIsTransitioning(false);
			}, 50);
		}, 300);
	};

	return (
		<div className="app">
			<link
				rel="stylesheet"
				href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css"
			/>
			<link
				rel="preconnect"
				href="https://fonts.googleapis.com"
			/>
			<link
				rel="preconnect"
				href="https://fonts.gstatic.com"
				crossOrigin="true"
			/>
			<link
				href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;700&display=swap"
				rel="stylesheet"
			/>

			<MatrixCanvas />

			<main className={`icerik-kart ${isTransitioning ? 'transitioning' : ''}`}>
				<HomePage isVisible={currentPage === 'home'} onNavigate={navigateTo} />
				<AboutPage isVisible={currentPage === 'about'} onNavigate={navigateTo} />
				<BlogPage isVisible={currentPage === 'blog'} onNavigate={navigateTo} />
				<MaintenancePage isVisible={currentPage === 'signature'} onNavigate={navigateTo} />
				<ContactPage isVisible={currentPage === 'contact'} onNavigate={navigateTo} />
			</main>

			<style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app {
          background-color: #000;
          color: #fff;
          font-family: 'Roboto Mono', monospace;
          margin: 0;
          padding: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          overflow-y: auto;
        }

        .icerik-kart {
          position: relative;
          z-index: 2;
          width: 90%;
          max-width: 800px;
          background-color: rgba(10, 10, 10, 0.85);
          border-radius: 5px;
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          margin: 5vh auto;
          text-align: center;
          min-height: auto;
          height: auto;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          padding: 0;
          transform: scale(1);
        }

        .icerik-kart.transitioning {
          opacity: 0.3;
          transform: scale(0.98);
          filter: blur(2px);
        }

        .page-content {
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          width: 100%;
          min-height: 600px;
          position: relative;
          padding: 50px;
          box-sizing: border-box;
          transform: translateY(0);
        }

        .page-content.page-visible {
          position: relative;
          opacity: 1;
          transform: translateY(0);
        }

        .page-content.page-hidden {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          opacity: 0;
          pointer-events: none;
          visibility: hidden;
          transform: translateY(20px);
        }

        .profil-resmi {
          width: 120px;
          height: 120px;
          background-color: #222;
          border-radius: 50%;
          margin: 0 auto 20px auto;
          border: 3px solid #fff;
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .profil-resmi:hover {
          transform: scale(1.05);
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.7);
        }

        .profil-resmi img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }

        h1 {
          font-size: 2.5rem;
          margin-bottom: 10px;
          font-weight: 700;
          animation: fadeInUp 0.8s ease-out;
        }

        h2 {
          color: #ccc;
          font-size: 1.5rem;
          margin-bottom: 20px;
          font-weight: 400;
          animation: fadeInUp 0.8s ease-out 0.2s both;
        }

        p {
          color: #ccc;
          line-height: 1.8;
          max-width: 550px;
          margin: 35px auto 45px auto;
          animation: fadeInUp 0.8s ease-out 0.4s both;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .sosyal-linkler {
          display: flex;
          justify-content: center;
          gap: 15px;
          flex-wrap: wrap;
          animation: fadeInUp 0.8s ease-out 0.6s both;
        }

        .nav-btn, .sosyal-linkler a {
          color: #ccc;
          background-color: rgba(20, 20, 20, 0.7);
          text-decoration: none;
          margin: 0;
          padding: 12px 25px;
          border: none;
          border-radius: 5px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          font-family: inherit;
          font-size: inherit;
          position: relative;
          overflow: hidden;
        }

        .nav-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transition: left 0.5s;
        }

        .nav-btn:hover::before {
          left: 100%;
        }

        .nav-btn:hover, .sosyal-linkler a:hover {
          color: #fff;
          background-color: rgba(30, 30, 30, 0.9);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
          transform: translateY(-5px) scale(1.02);
        }

        .nav-btn i, .sosyal-linkler i {
          margin-right: 8px;
          transition: transform 0.3s ease;
        }

        .nav-btn:hover i {
          transform: scale(1.2);
        }

        .geri-link {
          position: absolute;
          top: 20px;
          left: 20px;
          color: #aaa;
          text-decoration: none;
          font-size: 0.9em;
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.3s ease;
          z-index: 10;
          padding: 8px 12px;
          border-radius: 5px;
          background-color: rgba(0, 0, 0, 0.3);
        }

        .geri-link:hover {
          color: #fff;
          background-color: rgba(0, 0, 0, 0.6);
          transform: translateX(-5px);
        }

        .gif-alani {
          width: 100%;
          margin: 30px 0;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 5px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .gif-alani:hover {
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
        }

        .gif-alani img {
          display: block;
          width: 100%;
          height: auto;
          transition: transform 0.3s ease;
        }

        .gif-alani:hover img {
          transform: scale(1.02);
        }

        .hakkimda-yazisi {
          text-align: left;
          margin: 40px 0;
          padding-bottom: 40px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .hakkimda-yazisi h2 {
          text-align: center;
          margin-bottom: 20px;
        }

        .hakkimda-yazisi p {
          margin: 0 0 20px 0;
          max-width: none;
        }

        .sosyal-medya-bolumu {
          margin: 40px 0 40px 0;
          padding-bottom: 20px;
        }

        .sosyal-ikonlar-buyuk {
          margin-top: 20px;
        }

        .sosyal-ikonlar-buyuk a {
          display: inline-block;
          color: #fff;
          font-size: 3rem;
          margin: 0 20px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          background: none;
          box-shadow: none;
          padding: 10px;
          border-radius: 50%;
        }

        .sosyal-ikonlar-buyuk a:hover {
          color: #667eea;
          transform: scale(1.2) rotate(10deg);
          background: rgba(102, 126, 234, 0.1);
          box-shadow: 0 0 30px rgba(102, 126, 234, 0.5);
        }

        .blog-baslik {
          margin-bottom: 50px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          padding-bottom: 30px;
        }

        .blog-baslik h1 {
          font-size: 2.5rem;
          color: #fff;
          margin-bottom: 10px;
        }

        .blog-baslik p {
          color: #aaa;
          font-size: 1.1em;
          margin: 10px auto;
        }

        .yazi-listesi {
          text-align: left;
          margin-bottom: 40px;
        }

        .yazi-ozeti {
          margin-bottom: 40px;
          padding-bottom: 40px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          padding: 20px;
          border-radius: 8px;
        }

        .yazi-ozeti:hover {
          background-color: rgba(255, 255, 255, 0.02);
          transform: translateY(-2px);
        }

        .yazi-listesi .yazi-ozeti:last-child {
          border-bottom: none;
          margin-bottom: 20px;
          padding-bottom: 20px;
        }

        .yazi-ozeti h2 {
          margin-bottom: 10px;
        }

        .yazi-ozeti h2 a {
          color: #fff;
          text-decoration: none;
          font-size: 1.5em;
          transition: all 0.3s ease;
        }

        .yazi-ozeti h2 a:hover {
          color: #667eea;
        }

        .yazi-meta {
          color: #888;
          font-size: 0.9em;
          margin: 10px 0;
        }

        .yazi-ozeti p {
          color: #ccc;
          line-height: 1.8;
          margin: 15px 0;
          max-width: none;
        }

        .devamini-oku {
          display: inline-block;
          margin-top: 15px;
          color: #fff;
          text-decoration: none;
          font-weight: 700;
          transition: all 0.3s ease;
          padding: 5px 10px;
          border-radius: 3px;
        }

        .devamini-oku:hover {
          transform: translateX(10px);
          color: #667eea;
          background-color: rgba(102, 126, 234, 0.1);
        }

        .maintenance-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 500px;
          text-align: center;
          animation: fadeInUp 0.8s ease-out;
        }

        .maintenance-icon {
          font-size: 4rem;
          color: #667eea;
          margin-bottom: 30px;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }

        .maintenance-title {
          font-size: 2.5rem;
          margin-bottom: 30px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .maintenance-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .maintenance-description {
          font-size: 1.1rem;
          color: #ccc;
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .feature-preview {
          background-color: rgba(102, 126, 234, 0.1);
          border: 1px solid rgba(102, 126, 234, 0.3);
          border-radius: 15px;
          padding: 30px;
          margin: 30px 0;
          text-align: left;
        }

        .feature-preview h3 {
          color: #667eea;
          margin-bottom: 20px;
          text-align: center;
          font-size: 1.3rem;
        }

        .feature-list {
          list-style: none;
          padding: 0;
        }

        .feature-list li {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
          color: #ccc;
          font-size: 1rem;
        }

        .feature-list li i {
          color: #667eea;
          margin-right: 15px;
          font-size: 1.2rem;
        }

        .maintenance-status {
          margin: 40px 0;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background-color: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 15px;
        }

        .progress-fill {
          width: 85%;
          height: 100%;
          background: linear-gradient(90deg, #667eea, #764ba2);
          border-radius: 10px;
          animation: progressAnimation 2s ease-out;
        }

        @keyframes progressAnimation {
          from { width: 0%; }
          to { width: 85%; }
        }

        .progress-text {
          color: #aaa;
          font-size: 0.9rem;
          margin: 0;
        }

        .contact-suggestion {
          margin-top: 40px;
          padding: 30px;
          background-color: rgba(0, 0, 0, 0.3);
          border-radius: 15px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .contact-suggestion p {
          color: #ccc;
          margin-bottom: 20px;
        }

        .contact-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          border: none;
          padding: 15px 30px;
          border-radius: 15px;
          font-family: 'Roboto Mono', monospace;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }

        .contact-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
        }

        .contact-btn i {
          font-size: 1.1rem;
        }

        .iletisim-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
          text-align: left;
          margin: 30px 0 40px 0;
        }

        .form-grup {
          display: flex;
          flex-direction: column;
        }

        .form-grup label {
          margin-bottom: 8px;
          color: #aaa;
          font-size: 0.9em;
        }

        .form-grup input,
        .form-grup textarea {
          background-color: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
          padding: 12px;
          border-radius: 5px;
          font-family: 'Roboto Mono', monospace;
          font-size: 1em;
          transition: all 0.3s ease;
        }

        .form-grup input:focus,
        .form-grup textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 15px rgba(102, 126, 234, 0.3);
          transform: scale(1.02);
        }

        .form-grup textarea {
          resize: vertical;
          min-height: 150px;
        }

        .gonder-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          border: none;
          padding: 15px;
          border-radius: 8px;
          font-family: 'Roboto Mono', monospace;
          font-weight: 700;
          font-size: 1em;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .gonder-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
        }

        .gonder-btn:disabled {
          background: #666;
          color: #999;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 768px) {
          .icerik-kart {
            margin: 2vh auto;
            width: 95%;
          }

          .page-content {
            padding: 30px 20px;
            min-height: 500px;
          }

          .sosyal-linkler {
            flex-direction: column;
            align-items: center;
          }

          .nav-btn, .sosyal-linkler a {
            width: 200px;
            text-align: center;
          }

          .sosyal-ikonlar-buyuk a {
            font-size: 2.5rem;
            margin: 0 15px;
          }

          h1 {
            font-size: 2rem;
          }

          .yazi-ozeti h2 a {
            font-size: 1.3em;
          }

          .maintenance-container {
            padding: 20px;
            min-height: 400px;
          }

          .maintenance-icon {
            font-size: 3rem;
          }

          .maintenance-title {
            font-size: 2rem;
          }

          .feature-preview {
            padding: 20px;
          }

          .contact-suggestion {
            padding: 20px;
          }

          .contact-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
		</div>
	);
};

export default App;
